import axiosInterceptor from '../../../api/interceptor/axiosClient';
import { InfringementDetectionAresTagsType, InfringementDetectionTagsType } from '../types';

type CreateClaimChartPayload = {
  infringementDetectionId: string;
  resultIds: string[];
};

type CreateNewClaimSectionPayload = {
  claimChartClaimId: string;
  text: string;
  position?: number;
};

type PutClaimSectionTextPayload = {
  sectionId: string;
  text: string;
};

type PutClaimSectionNewImagePayload = {
  claimChartResultId: string;
  imageName: string;
  image: any;
};

type AddImageToSectionPayload = {
  sectionId: string;
  imageId: string;
};

type ClaimSectionCommentPayload = {
  sectionId: string;
  comment: string;
};

export interface ImageCitationI {
  imageCitationId: string;
  imageId: string;
}

export interface ClaimChartCommentsI {
  commentId: string;
  comment: string;
}

export interface ClaimSectionI {
  sectionId: string;
  sectionPosition: number;
  feature: string;
  reasoning: string;
  createdAt: string;
  imageCitations: ImageCitationI[];
  comments: ClaimChartCommentsI[];
}

export interface ClaimChartClaimI {
  claimChartClaimId: string;
  patentClaimId: string;
  claimText: string;
  score: number;
  claimSections: ClaimSectionI[];
}

export interface ClaimChartResultI {
  claimChartResultId: string;
  patentId: string;
  claims: ClaimChartClaimI[];
}

interface ClaimChartInfoItemI {
  claimChartId: string;
  infringementDetectionResultId: string;
  patentId: string;
}

interface ClaimChartInfoI {
  claimChartInfo: ClaimChartInfoItemI[];
  infringementDetectionId: string;
}

interface ClaimChartImagesI {
  images: {
    imageId: string;
    imageTags: InfringementDetectionTagsType[];
    imageAreaTags: InfringementDetectionAresTagsType[];
    imageUrl: string;
  }[];
}

interface ClaimChartNewImageI {
  imageId: string;
  imageName: string;
  imageUrl: string;
}

export const createClaimChart = async (payload: CreateClaimChartPayload): Promise<string[]> => {
  const { data } = await axiosInterceptor.put(`infringement-detection/claim-chart/create`, { ...payload });

  return data;
};

export const getClaimChartInfo = async (infringementDetectionId: string): Promise<ClaimChartInfoI> => {
  const { data } = await axiosInterceptor.get(`/infringement-detection/claim-chart/${infringementDetectionId}/info`);

  return data;
};

export const getClaimChartImages = async (infringementDetectionId: string): Promise<ClaimChartImagesI> => {
  const { data } = await axiosInterceptor.get(`infringement-detection/claim-chart/${infringementDetectionId}/images`);

  return data;
};

export const getClaimChartResult = async (claimChartResultId: string): Promise<ClaimChartResultI> => {
  const { data } = await axiosInterceptor.get(`infringement-detection/claim-chart/${claimChartResultId}`);

  return data;
};

export const putClaimSectionText = async (payload: PutClaimSectionTextPayload) => {
  const { data } = await axiosInterceptor.put(`infringement-detection/claim-chart/text`, { ...payload });

  return data;
};

export const createNewClaimSection = async (payload: CreateNewClaimSectionPayload): Promise<string> => {
  const { data } = await axiosInterceptor.post(`infringement-detection/claim-chart/section`, { ...payload });

  return data;
};

export const deleteClaimSection = async (claimChartSectionId: string) => {
  const { data } = await axiosInterceptor.delete(`infringement-detection/claim-chart/section/${claimChartSectionId}`);

  return data;
};

export const putClaimSectionNewImage = async (
  payload: PutClaimSectionNewImagePayload,
): Promise<ClaimChartNewImageI> => {
  const { data } = await axiosInterceptor.post(
    `infringement-detection/claim-chart/image`,
    { ...payload },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return data;
};

export const addImageToSection = async (payload: AddImageToSectionPayload): Promise<string> => {
  const { data } = await axiosInterceptor.put(`infringement-detection/claim-chart/section-image`, { ...payload });

  return data;
};

export const deleteClaimChartSectionImage = async (sectionImageId: string) => {
  const { data } = await axiosInterceptor.delete(`/infringement-detection/claim-chart/section-image/${sectionImageId}`);
  return data;
};

export const addClaimSectionComment = async (payload: ClaimSectionCommentPayload): Promise<string> => {
  const { data } = await axiosInterceptor.post(`infringement-detection/claim-chart/comment`, { ...payload });

  return data;
};

export const deleteClaimSectionComment = async (commentId: string) => {
  return await axiosInterceptor.delete(`infringement-detection/claim-chart/comment/${commentId}`);
};

export const exportClaimChart = async (claimChartId: string): Promise<string> => {
  const { data } = await axiosInterceptor.get(`infringement-detection/claim-chart/export/${claimChartId}`);

  return data;
};
